import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    IonAlert,
    IonHeader,
    IonLoading,
    IonPage, IonToast,
} from "@ionic/react";
import {StyledFooter} from "../../components/pane/pane.style";
import Form from "../../components/form";
import Logo from "../../components/logo";
import {useHistory} from "react-router-dom";
import {
    StyledSocialButton,
    StyledWiteButton
} from "../../components/button/button.style";
import LogoffImage from "../../assets/images/log-out.svg";
import {
    StyledEmptyContainer,
    StyledIonContent,
    StyledIonTitle,
    StyledIonToolbar
} from "../../components/content/content.style";
import StaticPane from "../../components/pane/static-pane.component";
import {Preferences} from "@capacitor/preferences";
import {getLatestLocation, validate} from "@services/geolocation.service";
import LocationModal, {Location} from "@modals/location/location.component";
import {Geolocation} from "@capacitor/geolocation";
import {ToastModel} from "types/toast";
import {AndroidSettings, IOSSettings, NativeSettings} from "capacitor-native-settings";
import LocationLeaveModal from "@modals/location/locationLeave.component";

const LocalizationStartPage: React.FC = () => {

    const {t} = useTranslation();
    const history = useHistory();

    const header = useRef<HTMLIonHeaderElement>(null);
    const [topEdge, updateTopEdge] = useState<number | undefined>(undefined);
    const [latest, setLatest] = useState<Location>();
    const [showLocationModal, setShowLocationModal] = useState<boolean>(false);
    const [showLoading, setShowLoading] = useState(false);
    const [toast, setToast] = useState<ToastModel>();
    const [showPermissionAlert, setShowPermissionAlert] = useState<boolean>(false);
    const [showLocationLeaveModal, setShowLocationLeaveModal] = useState<boolean>(false);

    const updateHeight = () => {
        if (header.current ?.clientHeight == 0) {
            setTimeout(updateHeight);
        } else {
            updateTopEdge(header.current?.clientHeight);
        }
    };

    document.addEventListener('ionBackButton', (ev: Event) => {
        ev.stopPropagation();
    });

    useEffect(() => {
        updateHeight();
    });

    useEffect(() => {
        fetchLatest();
    }, []);

    const fetchLatest = async () => {
        await getLatestLocation().then(response => {
            setLatest(response.data);
        });
    }

    const skipLocalizationCheck = async () => {
        await Preferences.set({
            'key': 'skip_localization_check',
            'value': '1',
        });

        history.goBack()
    }

    const validateCurrent = async () => {
        let hasPermissions = await Geolocation.checkPermissions()
            .then((location) => {
                return location.location;
            })
            .catch(reason => {
                return 'error';
            });

        setShowLoading(true)
        if (hasPermissions === 'denied' || hasPermissions === 'error') {
            setShowPermissionAlert(true);
        } else {
            await validate()
                .then((response) => {
                    history.goBack()
                })
                .catch(reason => {
                    setToast({
                        message: t('localizationStart.extendExpiringError'),
                        type: 'danger'
                    });
                })
        }
        setShowLoading(false)
    }

    const postNew = async () => {
        setShowLocationModal(false);
        history.goBack();
    }

    const postLeaveCurrent = async () => {
        setShowLocationLeaveModal(false);
        fetchLatest();
    }

    return (

        <IonPage>
            <StyledIonContent>
                <IonHeader ref={header} className="ion-no-border">
                    <StyledIonToolbar>
                        <StyledIonTitle>{t("localizationStart.title")}</StyledIonTitle>
                    </StyledIonToolbar>
                </IonHeader>
                <StaticPane topEdge={topEdge} marginTop={40} paddingBottom={97}>
                    <StyledEmptyContainer>
                    <Logo />
                        {(!latest || (latest && !latest.is_expiring)) && <p className="center">{t('localizationStart.description')}</p>}

                        {latest && latest.is_expiring &&
                            <>
                                <p className="center">{t('localizationStart.latestLocalization')}: {latest.client.name}</p>
                                <StyledSocialButton className="auto-width" onClick={validateCurrent}>
                                    {t('localizationStart.extendExpiring')}
                                </StyledSocialButton>

                                <StyledSocialButton className="auto-width" onClick={() => setShowLocationLeaveModal(true)}>
                                    {t('localizationStart.leaveExpiring')}
                                </StyledSocialButton>
                            </>
                        }

                    {(!latest || (latest && !latest.is_expiring)) && <StyledFooter className="no-shadow">
                        <Form.Button type="submit" onClick={() => setShowLocationModal(true)}>{t('localizationStart.startNew')}</Form.Button>
                        <StyledWiteButton onClick={skipLocalizationCheck}>{t("localizationStart.skip")} <img src={LogoffImage}/></StyledWiteButton>
                    </StyledFooter>}
                    </StyledEmptyContainer>
                </StaticPane>
            </StyledIonContent>

            <IonAlert
                isOpen={showPermissionAlert}
                onDidDismiss={() => setShowPermissionAlert(false)}
                header={t('common.geolocation_permissions.alert')}
                buttons={[
                    {
                        text: t('common.alertCancel'),
                        role: 'cancel',
                        cssClass: 'secondary',
                        handler: () => {
                            setShowPermissionAlert(false);
                        },
                    },
                    {
                        text: t('common.geolocation_permissions.goToAppSettings'),
                        handler: async () => {
                            setShowPermissionAlert(false);
                            await NativeSettings.open({
                                optionAndroid: AndroidSettings.ApplicationDetails,
                                optionIOS: IOSSettings.App
                            })
                        }
                    }
                ]}
            />

            <IonLoading onDidDismiss={() => setShowLoading(false)} isOpen={showLoading}/>

            <IonToast
                isOpen={!!toast}
                onDidDismiss={() => setToast(undefined)}
                message={toast?.message}
                duration={toast?.duration ? toast.duration : 6000}
                position="top"
                color={toast?.type}
            />

            <LocationModal isOpen={showLocationModal} onClose={postNew} onSuccess={postNew} />
            <LocationLeaveModal isOpen={showLocationLeaveModal} onClose={postLeaveCurrent} />
        </IonPage>
    );
};

export default LocalizationStartPage;